import { Container, Typography, Box, Divider } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import {
  Groups,
  Campaign,
  ConnectWithoutContact,
  ArrowForward,
} from "@mui/icons-material";

const featureGroups = [
  {
    title: "See the Whole Picture",
    link: "/features/",
    icon: <Groups />,
    gradient:
      "linear-gradient(135deg, rgba(56,189,248,0.55), rgba(15,23,42,0.0))",
    items: [
      {
        title: "One Shared View of Your Community",
        description:
          "Keep the people, households and relationships your church encounters visible to the wider evangelism team.",
      },
      {
        title: "One Shared Ministry Memory",
        description:
          "Bring useful outreach notes out of spreadsheets, WhatsApp chats and individual memories into one secure system.",
      },
      {
        title: "See Each Person's Journey",
        description:
          "Understand what has happened so far, where someone is in their journey and what a helpful next step might be.",
      },
    ],
  },
  {
    title: "Lead with Intention",
    link: "/features/",
    icon: <Campaign />,
    gradient:
      "linear-gradient(135deg, rgba(168,85,247,0.55), rgba(15,23,42,0.0))",
    items: [
      {
        title: "Connect Your Evangelism",
        description:
          "Bring events, outreach, courses and follow-up together as parts of an intentional evangelism strategy.",
      },
      {
        title: "Coordinate Your Teams",
        description:
          "Help staff and volunteers work from the same picture instead of relying on informal handovers.",
      },
      {
        title: "Learn What Is Happening",
        description:
          "Compare activity and outcomes over time so your decisions can be informed by more than anecdotes.",
      },
    ],
  },
  {
    title: "Never Lose the Next Step",
    link: "/features/",
    icon: <ConnectWithoutContact />,
    gradient:
      "linear-gradient(135deg, rgba(34,197,94,0.45), rgba(15,23,42,0.0))",
    items: [
      {
        title: "Capture Gospel Conversations",
        description:
          "Let your team record meaningful interactions while the details are still fresh.",
      },
      {
        title: "Connect Events to Relationships",
        description:
          "Record interest, attendance and follow-up so an event does not become the end of the story.",
      },
      {
        title: "Coordinate Follow-Up",
        description:
          "Give your team the context they need to follow people up warmly, consistently and intentionally.",
      },
    ],
  },
];

const Features = () => {
  return (
    <Box
      component="section"
      id="features"
      sx={{
        width: "100%",
        py: { xs: 10, md: 14 },
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.65,
          background:
            "radial-gradient(900px 420px at 20% 20%, rgba(77, 222, 128, 0.35), transparent 60%), radial-gradient(800px 360px at 80% 30%, rgba(77, 222, 128, 0.35), transparent 60%)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: "center",
            color: "white",
            fontWeight: 900,
            letterSpacing: -0.7,
            mb: 2,
          }}
        >
          One System for Managing Your Church&apos;s Evangelism
        </Typography>

        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            color: "rgba(255,255,255,0.82)",
            maxWidth: 760,
            mx: "auto",
            mb: { xs: 6, md: 9 },
            lineHeight: 1.6,
          }}
        >
          Connect your individual evangelistic efforts into one intentional pathway. From publicity and first contact to deep conversations and ongoing follow-up
        </Typography>

        <Grid2 container spacing={{ xs: 3, md: 4 }}>
          {featureGroups.map((group) => (
            <Grid2 size={{ xs: 12, md: 4 }} key={group.title}>
              <Box
                sx={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 5,
                  overflow: "hidden",
                  height: "100%",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
                  transition: "transform 0.25s ease, border-color 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "rgba(255,255,255,0.18)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 3,
                    py: 2.75,
                    color: "white",
                    background: group.gradient,
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      width: 38,
                      height: 38,
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.12)",
                    }}
                  >
                    {group.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={900}>
                    {group.title}
                  </Typography>
                </Box>

                <Box>
                  {group.items.map((item, idx) => (
                    <Box key={item.title}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 3,
                          py: 2.75,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            background: "rgba(255,255,255,0.05)",
                          },
                        }}
                      >
                        <Box pr={2}>
                          <Typography fontWeight={900} sx={{ color: "white" }}>
                            {item.title}
                          </Typography>
                          <Typography
                            fontWeight={400}
                            sx={{
                              color: "rgba(255,255,255,0.76)",
                              mt: 0.6,
                              lineHeight: 1.55,
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Box>

                        <ArrowForward sx={{ color: "rgba(255,255,255,0.60)" }} />
                      </Box>

                      {idx < group.items.length - 1 && (
                        <Divider
                          sx={{
                            mx: 3,
                            borderColor: "rgba(255,255,255,0.12)",
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid2>
          ))}
        </Grid2>
      </Container>
    </Box>
  );
};

export default Features;